import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { InputFieldType, StateSchema, renderHeadline, slugifyKey } from '../servicesShared';
import { DirtySetter } from './editorShared';

export interface EditableStateField {
    name: string;
    type: InputFieldType;
    description: string;
}

export const stateSchemaToFields = (schema?: Partial<StateSchema> | null): EditableStateField[] =>
    Object.entries(schema?.fields || {}).map(([name, spec]) => ({
        name,
        type: spec.type,
        description: spec.description || '',
    }));

export const fieldsToStateSchema = (fields: EditableStateField[], headline: string): StateSchema => {
    const out: StateSchema = { fields: {}, headline: headline.trim() || null };
    fields
        .filter((field) => field.name.trim())
        .forEach((field) => {
            out.fields[field.name.trim()] = {
                type: field.type,
                ...(field.description.trim() ? { description: field.description.trim() } : {}),
            };
        });
    return out;
};

const SAMPLE_VALUES: Record<InputFieldType, any> = { string: 'text', number: 1.5, integer: 3, boolean: true, array: ['a', 'b'] };

/**
 * The typed headline fields the job page renders plus the headline template
 * ("{done} / {total} locations compliant"). set_state validates these; the
 * job keeps everything else in free-form details.
 */
export const StateSchemaEditor: React.FC<{
    fields: EditableStateField[];
    onFieldsChange: DirtySetter<EditableStateField[]>;
    headline: string;
    onHeadlineChange: (value: string) => void;
}> = ({ fields, onFieldsChange, headline, onHeadlineChange }) => {
    const update = (index: number, patch: Partial<EditableStateField>) =>
        onFieldsChange((prev) => prev.map((existing, i) => (i === index ? { ...existing, ...patch } : existing)));
    const sample = Object.fromEntries(fields.filter((f) => f.name.trim()).map((f) => [f.name.trim(), SAMPLE_VALUES[f.type]]));
    const unknownRefs = Array.from(headline.matchAll(/\{([a-zA-Z][a-zA-Z0-9_]*)/g))
        .map((match) => match[1])
        .filter((name) => !fields.some((f) => f.name.trim() === name));

    return (
        <div className="space-y-3">
            {fields.map((field, index) => (
                <div key={index} className="rounded-lg border p-3 space-y-2">
                    <div className="flex items-center gap-2">
                        <Input
                            className="font-mono text-sm"
                            placeholder="field_name"
                            value={field.name}
                            onChange={(event) => update(index, { name: slugifyKey(event.target.value) })}
                        />
                        <Select value={field.type} onValueChange={(value) => update(index, { type: value as InputFieldType })}>
                            <SelectTrigger className="w-[130px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="string">Text</SelectItem>
                                <SelectItem value="number">Number</SelectItem>
                                <SelectItem value="integer">Integer</SelectItem>
                                <SelectItem value="boolean">Yes / No</SelectItem>
                                <SelectItem value="array">List</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" onClick={() => onFieldsChange((prev) => prev.filter((_, i) => i !== index))}>
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                    </div>
                    <Input
                        placeholder="What this number means (shown under the value)"
                        value={field.description}
                        onChange={(event) => update(index, { description: event.target.value })}
                    />
                </div>
            ))}
            <Button
                variant="outline"
                size="sm"
                onClick={() => onFieldsChange((prev) => [...prev, { name: '', type: 'integer', description: '' }])}
                disabled={fields.length >= 24}
            >
                <Plus className="h-4 w-4 mr-1" /> Add state field
            </Button>

            <div className="space-y-1 pt-2">
                <Label htmlFor="state-headline">Headline template</Label>
                <Input
                    id="state-headline"
                    value={headline}
                    maxLength={200}
                    placeholder="{compliant_locations} / {total_locations} locations compliant"
                    onChange={(event) => onHeadlineChange(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                    Reference declared fields in braces. Preview:{' '}
                    <span className="text-foreground">{renderHeadline(headline, sample) || '—'}</span>
                    {unknownRefs.length > 0 && (
                        <span className="text-red-500"> · unknown field{unknownRefs.length === 1 ? '' : 's'}: {unknownRefs.join(', ')}</span>
                    )}
                </p>
            </div>
        </div>
    );
};

export default StateSchemaEditor;
